import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { Clip, ClipsMap } from 'components/app/editor/_mediator/editor.slice';
import { handleClips } from 'components/app/editor/_mediator/editor.slice';
import { parseClipId } from 'components/app/_helpers/_app.helper';
import { clipsApi } from 'components/app/editor/_mediator/editor-clips/editor-clips.helper';
import {
  handleSbLinks,
  resolvePinInsertPosition,
} from 'components/app/editor/_mediator/editor-clips/editor-clips-sb.helper';
import { setSbPosition } from 'components/app/editor/_mediator/editor-controls.slice';
import { StoryBoard, setStoryboard } from 'services/storyboard.service';
import { setClips } from 'services/clip.service';
import { Error } from 'components/app/_helpers/app-errors.helper';
import { selectThumbnailData } from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { toast } from 'react-toastify';

/** Replace every occurrence of `oldId` with `newId` in a just-built storyboard patch. */
const renameInStoryBoard = (storyBoard: Partial<StoryBoard>, oldId: ClipId, newId: ClipId): Partial<StoryBoard> =>
  Object.fromEntries(
    Object.entries(storyBoard).map(([track, cells]) => [track, cells.map((cell) => (cell === oldId ? newId : cell))])
  ) as Partial<StoryBoard>;

/** Replace `oldId` with `newId` in a ClipId[] array. */
const renameInIdList = (ids: ClipId[], oldId: ClipId, newId: ClipId): ClipId[] =>
  ids.map((id) => (id === oldId ? newId : id));

/**
 * Hook for pinning/unpinning highlights to/from the storyboard.
 * `clipData` is required for `togglePin` / pin; `unpinHighlight` accepts an explicit clip.
 */
export const useHighlightPin = (clipData?: Clip) => {
  const dispatch = useAppDispatch();
  const selectedStory = useAppSelector((state) => state.projects.selectedStory);
  const storyBoard = useAppSelector((state) => state.editor.storyBoard);
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const selector = useAppSelector((state) => state.editor.selector);
  const linkedClips = useAppSelector((state) => state.editor.linkedClips);
  const activeClip = useAppSelector((state) => state.editor.activeClip);
  const sbPosition = useAppSelector((state) => state.editor__controls.sbPosition);
  const clipThumbnails = useAppSelector((state) => state.clipThumb.clipThumbnails);

  const togglePin = async () => {
    if (!clipData || !selectedStory || !clipData.mediaFileId || clipData.startTime == null) {
      Error.addClip.invalidStartTime();
      return;
    }

    if (clipData.isPinned ?? false) {
      await unpinHighlight(clipData);
    } else {
      await pinHighlight();
    }
  };

  const pinHighlight = async () => {
    if (!clipData || !clipsMap || !selector || !storyBoard || !clipData.highlightId) {
      return;
    }

    // Create a new clip from the highlight data with isPinned = true
    const clip: Clip = {
      ...clipData,
      clipGroupId: clipData.clipGroupId || 0,
      isPinned: true,
    };

    // When pinning a highlight, it's already in the selector, so we don't want to add it again
    // Use clipsApi().add() but then manually fix the selector to avoid duplicates
    const position = resolvePinInsertPosition(storyBoard, activeClip, sbPosition);
    const res = clipsApi(selector, storyBoard, linkedClips).add([clip], position);
    if (!res) {
      Error.addClip.noStoryboardCell();
      return;
    }

    // Check if clip is already in selector to avoid duplicates
    const fileClips = selector[clip.mediaFileId] || [];
    const isAlreadyInSelector = fileClips.includes(clip.clipId);

    // If already in selector, don't add it again (keep original selector)
    // Otherwise, use the updated selector from clipsApi
    const finalSelector = isAlreadyInSelector ? selector : res.selector;

    const res1 = handleSbLinks(res.linkedClips, res.storyBoard, storyBoard);
    if (res1.status === 'cantBeFixed') {
      Error.handleSbLinks();
      return;
    }

    try {
      // Get thumbnail URL from clip thumbnails state
      const thumbnailData = selectThumbnailData(clip.clipId, clipThumbnails);
      const thumbnailUrl = thumbnailData?.url || null;

      // Prepare clip payload with highlightId - ONLY the single clip being pinned
      const clipPayload: Record<
        string,
        {
          clipGroupId: number;
          config: Clip['config'];
          thumbnailUrl: string | null;
          score?: number;
          highlightId?: number;
        }
      > = {
        [clip.clipId]: {
          clipGroupId: clip.clipGroupId,
          config: clip.config,
          thumbnailUrl: thumbnailUrl,
          score: clip.score,
          highlightId: clip.highlightId,
        },
      };

      // Verify we're only sending one clip
      const clipIds = Object.keys(clipPayload);
      if (clipIds.length !== 1 || clipIds[0] !== clip.clipId) {
        toast.error('Error: Invalid clip payload');
        return;
      }

      // Update clips via API - only the single pinned clip
      // NOTE: Frontend correctly sends only ONE clip, but backend may be creating
      // clips for all highlights in the file. This needs to be investigated on the backend.
      const setClipsRes = await setClips({
        storyId: selectedStory!,
        data: clipPayload,
      });

      // Backend may reuse an existing (times-derived) clip identity for this highlight on an
      // idempotent re-pin instead of the client's key (#331). Reconcile local identity to match
      // so storyboard membership and later SetClips lookups stay aligned with the DB row (#496).
      const resolvedClipId = setClipsRes.data.resolvedFrontEndIds?.[clip.clipId] ?? clip.clipId;
      const isResolved = resolvedClipId !== clip.clipId;

      // res.clipsMap is always the single-entry { [clip.clipId]: clip } produced by clipsApi().add([clip]).
      // setEditorClips merges clipsMap per key and only deletes an entry on an explicit null, so the
      // stale client-keyed entry must be nulled out here or it lingers alongside the resolved one.
      const finalClipsMap: ClipsMap = isResolved
        ? {
            [resolvedClipId]: { ...clip, ...parseClipId(resolvedClipId), clipId: resolvedClipId },
            [clip.clipId]: null,
          }
        : res.clipsMap;
      const finalSelectorState = isResolved
        ? {
            ...finalSelector,
            [clip.mediaFileId]: renameInIdList(finalSelector[clip.mediaFileId] || [], clip.clipId, resolvedClipId),
          }
        : finalSelector;
      const finalStoryBoard = isResolved
        ? renameInStoryBoard(res1.storyBoard, clip.clipId, resolvedClipId)
        : res1.storyBoard;
      const linkedGroup = res.linkedClips[clip.clipGroupId];
      const finalLinkedClips =
        isResolved && linkedGroup
          ? { ...res.linkedClips, [clip.clipGroupId]: renameInIdList(linkedGroup, clip.clipId, resolvedClipId) }
          : res.linkedClips;

      // Update storyboard
      await setStoryboard({
        storyId: selectedStory!,
        ...finalStoryBoard,
      });

      // Update local state
      // The clip already has isPinned = true from above, and clipsApi.add() will add it to clipsMap
      dispatch(
        handleClips({
          data: {
            clipsMap: finalClipsMap,
            linkedClips: finalLinkedClips,
            selector: finalSelectorState,
            storyBoard: finalStoryBoard,
          },
          backupParams: { title: 'Add Clip', action: 'addClip', activeClip: [activeClip, resolvedClipId] },
        })
      );
      if (position) dispatch(setSbPosition(position));
    } catch (error) {
      toast.error('Failed to pin highlight to storyboard');
    }
  };

  /** Unpin a highlight from the storyboard; keeps it in clipsMap/selector for re-pin. */
  const unpinHighlight = async (clipToUnpin?: Clip) => {
    const target = clipToUnpin ?? clipData;
    if (!clipsMap || !storyBoard || !target?.clipId || !selector || !selectedStory) return;

    const clip = clipsMap[target.clipId];
    if (!clip) return;

    const res = clipsApi(selector, storyBoard, linkedClips).del([clip]);
    const res1 = handleSbLinks(res.linkedClips, res.storyBoard, storyBoard);

    try {
      await setStoryboard({
        storyId: selectedStory,
        ...res1.storyBoard,
      });

      // Preserve clip in clipsMap/selector with isPinned=false (do not use res.clipsMap/selector delete)
      const updatedClipsMap = {
        ...res.clipsMap,
        [target.clipId]: { ...clip, isPinned: false },
      };

      dispatch(
        handleClips({
          data: { ...res, clipsMap: updatedClipsMap, selector: selector, storyBoard: res1.storyBoard },
          backupParams: { title: 'Delete Clip', action: 'delClip', activeClip: [target.clipId, null] },
        })
      );
    } catch (error) {
      console.error('Failed to unpin highlight:', error);
      toast.error('Failed to unpin highlight from storyboard');
    }
  };

  return { togglePin, unpinHighlight };
};
